import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useTheme,
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from "@mui/material";
import ColoredLogo from "../misc/ColoredLogo";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArticleIcon from "@mui/icons-material/Article";
import MemoryIcon from "@mui/icons-material/Memory";
import WorkIcon from "@mui/icons-material/Work";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";
import LanguageIcon from "@mui/icons-material/Language";
import { Link as RouterLink } from "react-router-dom";
import siteData from "../../data/site.json";

export default function Navbar() {
  const theme = useTheme();
  const openToWork = siteData.openToWork;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navLinks = [
    { label: "Blog", to: "/blog", icon: <ArticleIcon /> },
    { label: "Technologies", to: "/technologies", icon: <MemoryIcon /> },
    { label: "Experience", to: "/experience", icon: <WorkIcon /> },
  ];

  const portfolioLinks = [
    {
      label: "Gameplay Portfolio",
      to: "/p/gameplay",
      icon: <SportsEsportsIcon />,
    },
    { label: "UI/UX Portfolio", to: "/p/ui", icon: <DesignServicesIcon /> },
    { label: "3D Design Portfolio", to: "/p/3d", icon: <ThreeDRotationIcon /> },
    { label: "Web Portfolio", to: "/p/web", icon: <LanguageIcon /> },
  ];

  return (
    <AppBar
      position="static"
      elevation={0}
      color="default"
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", flexWrap: "wrap" }}>
        {/* Left: Logo + Name + Status */}
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            component={RouterLink}
            to="/"
            sx={{ textDecoration: "none", color: "inherit" }}
          >
            <ColoredLogo
              src="/logo.svg"
              alt="Logo"
              color={theme.palette.primary.main}
              size={32}
              style={{ marginRight: 20 }}
            />
            <Typography variant="h6" fontWeight={600}>
              Aaron Hunt
            </Typography>
          </Box>

          <Chip
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: openToWork ? "success.main" : "text.disabled",
                  }}
                />
                {openToWork ? "Open for Work" : "Not Open for Work"}
              </Box>
            }
            color={openToWork ? "success" : "default"}
            variant="outlined"
            size="small"
            sx={{ ml: "20px", cursor: "pointer" }}
            component="a"
            href="https://www.linkedin.com/in/aaron-h-36332412b/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {}} // prevents error
          />
        </Box>

        {/* Desktop Nav */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
          }}
        >
          {navLinks.map((link) => (
            <Button
              key={link.to}
              component={RouterLink}
              to={link.to}
              color="inherit"
            >
              {link.label}
            </Button>
          ))}
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/contact"
            endIcon={<ArrowForwardIosIcon sx={{ height: 14 }} />}
          >
            Contact
          </Button>
        </Box>

        {/* Mobile Hamburger */}
        <IconButton
          sx={{ display: { xs: "flex", md: "none" } }}
          edge="end"
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon />
        </IconButton>

        {/* Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <Box
            sx={{
              width: 260,
              backgroundColor: theme.palette.background.default,
              height: "100%",
              color: theme.palette.text.primary,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
            role="presentation"
            onClick={() => setDrawerOpen(false)}
            onKeyDown={() => setDrawerOpen(false)}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Navigation
              </Typography>
              <List>
                {navLinks.map(({ label, to, icon }) => (
                  <ListItem
                    key={to}
                    button
                    component={RouterLink}
                    to={to}
                    sx={{ color: "inherit" }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 32, color: "inherit", opacity: 0.5 }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText primary={label} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Portfolios
              </Typography>
              <List>
                {portfolioLinks.map(({ label, to, icon }) => (
                  <ListItem
                    key={to}
                    button
                    component={RouterLink}
                    to={to}
                    sx={{ color: "inherit" }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 32, color: "inherit", opacity: 0.5 }}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText primary={label} />
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Contact Button at Bottom */}
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                component={RouterLink}
                to="/contact"
                endIcon={<ArrowForwardIosIcon sx={{ height: 14 }} />}
              >
                Contact
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
}
